<template>
	<header-block :title="isSuggested ? 'View policy' : 'Your policy'" border="0" :edit="!isSuggested && !editMode" :del="!isSuggested && !editMode" @edit="loadEditSections" @delete="handleHeaderDelete" menuDescription="Would you like to edit or delete this policy?"></header-block>
	<CustomerLoadWait :customer="policy" />

	<!-- Header action button: X (edit mode only) -->
	<div v-if="policy && !isSuggested && editMode" class="header-action-btn" :style="'top: ' + headerActionTop + 'px;'" @click="editMode = false">
		<v-icon icon="mdi-close" size="20" />
	</div>

		<div v-if="policy" class="policy-page">

			<!-- Policy Card -->
			<div class="policy-card-container">
				<AccountListItem
					:provider-name="properCase(policy.provider?.name) || 'Provider'"
					:line1="policy.policyType?.name || 'Insurance'"
					:line2="policy.policyNumber || ''"
					:label="cardLabel"
					:value="cardValue"
					:label2="cardLabel2"
					:value2="cardValue2"
					:avatar-url="policy.provider?.name ? _getBrandLogoUrl(policy.provider.name) : require('@/assets/round1.png')"
					:badge-url="isSuggested ? require('@/assets/aisummary.png') : null"
				/>
			</div>

			<!-- Tabs (shown for suggested assets) -->
			<div v-if="isSuggested" class="tabs">
				<div class="tab" :class="{ active: activeTab === 'summary' }" @click="activeTab = 'summary'">Details</div>
				<div class="tab" :class="{ active: activeTab === 'documents' }" @click="activeTab = 'documents'">Documents</div>
			</div>

			<!-- ==================== SUMMARY TAB ==================== -->
			<div v-if="activeTab === 'summary'" class="summary-content">

				<!-- ===== SUGGESTED ASSET VIEW ===== -->
				<template v-if="isSuggested">

					<!-- Added from documents banner -->
					<div class="suggested-banner-group">
						<div class="suggested-banner">
							<img :src="require('@/assets/aisummary-noborder.png')" class="suggested-banner-icon" />
							<span class="suggested-banner-text">Added from your documents</span>
						</div>
						<p class="suggested-banner-description">This policy was automatically created from files you uploaded. Manage it in <a href="/#/documents" class="suggested-link">My Documents</a>.</p>
					</div>

					<!-- Flat field list - only show non-null fields -->
					<div class="fields-list">
						<div class="suggested-field-card" v-for="field in suggestedFields" :key="field.label">
							<span class="suggested-field-label">{{ properCase(field.label) }}</span>
							<span class="suggested-field-value">{{ properCase(field.value) }}</span>
						</div>
					</div>

					<!-- Delete Policy -->
					<!-- <MGButton
						text="Delete policy"
						:fullWidth="true"
						:arrow="true"
						@click="showDeleteConfirm = true"
					/> -->

					<!-- Last updated -->
					<!-- <div class="last-updated-row" v-if="suggestedAsset?.fromDate">						
						<p class="last-updated">Last updated: {{ formatDate(suggestedAsset.fromDate) }}.</p>
					</div> -->

				</template>

				<!-- ===== VIEW MODE ===== -->
				<template v-if="!editMode && !isSuggested">

					<!-- Progress Dial (A/B test: v1 or v2 on each refresh) -->
					<ProgressDial
						v-if="policy.startDate && policy.endDate"
						:fromDate="policy.startDate"
						:toDate="policy.endDate"
						title="Days until renewal"
						footerLabel="Renewal date"
					/>

					<!-- Policy details -->
					<div class="section-group" v-if="policy.policyType?.name || policy.provider?.name || policy.startDate || policy.payFrequency || policy.policyNumber">
						<h2 class="section-title">Policy details</h2>
						<div class="fields-list">
							<div class="field-card" v-if="policy.policyType?.name">
								<span class="field-label">Type</span>
								<span class="field-value">{{ policy.policyType.name }}</span>
							</div>
							<div class="field-card" v-if="policy.provider?.name">
								<span class="field-label">Provider</span>
								<span class="field-value">{{ properCase(policy.provider.name) }}</span>
							</div>
							<div class="field-card" v-if="policy.startDate">
								<span class="field-label">Start date</span>
								<span class="field-value">{{ formatDate(policy.startDate) }}</span>
							</div>
							<div class="field-card" v-if="policy.payFrequency">
								<span class="field-label">Pay frequency</span>
								<span class="field-value">{{ policy.payFrequency }}</span>
							</div>
							<div class="field-card" v-if="policy.policyNumber">
								<span class="field-label">Policy number</span>
								<span class="field-value">{{ policy.policyNumber }}</span>
							</div>
						</div>
					</div>

					<!-- What's insured -->
					<div class="section-group" v-if="hasInsuredDetails">
						<h2 class="section-title">What's insured</h2>
						<p class="section-subtitle">The home, person, pet, or thing this policy is for</p>
						<div class="fields-list">
							<!-- Buildings & Contents -->
							<template v-if="isBuildingsOrContents">
								<div class="field-card" v-if="insuredAddress">
									<span class="field-label">Property address</span>
									<span class="field-value">{{ insuredAddress }}</span>
								</div>
								<div class="field-card" v-if="policy.insuredItems[0]?.valueInsured">
									<span class="field-label">Value insured</span>
									<span class="field-value">{{ formatter.format(policy.insuredItems[0].valueInsured) }}</span>
								</div>
							</template>
							<!-- Vehicle -->
							<template v-if="policy.policyType?.name === 'Vehicle'">
								<div class="field-card" v-if="policy.insuredItems[0]?.vehicleMake">
									<span class="field-label">Make</span>
									<span class="field-value">{{ policy.insuredItems[0].vehicleMake }}</span>
								</div>
								<div class="field-card" v-if="policy.insuredItems[0]?.vehicleModel">
									<span class="field-label">Model</span>
									<span class="field-value">{{ policy.insuredItems[0].vehicleModel }}</span>
								</div>
								<div class="field-card" v-if="policy.insuredItems[0]?.vehicleRegistration">
									<span class="field-label">Registration</span>
									<span class="field-value">{{ policy.insuredItems[0].vehicleRegistration }}</span>
								</div>
							</template>
							<!-- Pet -->
							<template v-if="policy.policyType?.name === 'Pet'">
								<div class="field-card" v-if="policy.insuredItems[0]?.name">
									<span class="field-label">Name</span>
									<span class="field-value">{{ policy.insuredItems[0].name }}</span>
								</div>
								<div class="field-card" v-if="policy.insuredItems[0]?.petBreed">
									<span class="field-label">Breed</span>
									<span class="field-value">{{ policy.insuredItems[0].petBreed }}</span>
								</div>
							</template>
						</div>
					</div>

					<!-- Cover & limits -->
					<div class="section-group" v-if="hasCoverDetails">
						<h2 class="section-title">Cover & limits</h2>
						<p class="section-subtitle">What's covered and where the limits apply</p>
						<div class="fields-list">
							<div class="field-card" v-if="policy.annualPremium">
								<span class="field-label">Annual premium</span>
								<span class="field-value">{{ formatter.format(policy.annualPremium) }}</span>
							</div>
							<div class="field-card" v-if="policy.monthlyPremium">
								<span class="field-label">Monthly premium</span>
								<span class="field-value">{{ formatter.format(policy.monthlyPremium) }}</span>
							</div>
							<div class="field-card" v-if="policy.sumAssured">
								<span class="field-label">Sum insured</span>
								<span class="field-value">{{ formatter.format(policy.sumAssured) }}</span>
							</div>
							<div class="field-card" v-if="policy.excess">
								<span class="field-label">Excess</span>
								<span class="field-value">{{ formatter.format(policy.excess) }}</span>
							</div>
							<div class="field-card" v-if="policy.voluntaryExcess">
								<span class="field-label">Voluntary excess</span>
								<span class="field-value">{{ formatter.format(policy.voluntaryExcess) }}</span>
							</div>
						</div>
					</div>

					<!-- Additional details (collapsible) -->
					<div class="additional-section" v-if="hasAdditionalDetails" @click="showAdditional = !showAdditional">
						<div class="additional-header">
							<span class="section-title">Additional details</span>
							<v-icon :icon="showAdditional ? 'mdi-chevron-up' : 'mdi-chevron-down'" size="16" />
						</div>
						<div v-if="showAdditional" class="fields-list">
							<div class="field-card" v-if="policy.soleOrJoint">
								<span class="field-label">Sole or joint</span>
								<span class="field-value">{{ policy.soleOrJoint }}</span>
							</div>
							<div class="field-card" v-if="policy.coverType">
								<span class="field-label">Cover type</span>
								<span class="field-value">{{ policy.coverType }}</span>
							</div>
							<div class="field-card" v-if="policy.noClaimsYears">
								<span class="field-label">No claims (years)</span>
								<span class="field-value">{{ policy.noClaimsYears }}</span>
							</div>
							<div class="field-card" v-if="policy.travelCoverType">
								<span class="field-label">Travel cover type</span>
								<span class="field-value">{{ policy.travelCoverType }}</span>
							</div>
							<div class="field-card" v-if="policy.travelCoverage">
								<span class="field-label">Travel coverage</span>
								<span class="field-value">{{ policy.travelCoverage }}</span>
							</div>
						</div>
					</div>

					<!-- WhatsApp Promo Card -->
					<WhatsAppPromoCard
						ref="whatsAppPromoCard"
						v-if="showWhatsappCard"
						title="Questions about your policy?"
						subtitle="Chat with our WhatsApp assistant about this policy or send us documents."
						buttonText="Chat on WhatsApp"
						:closable="true"
						@close="showWhatsappCard = false"
						@action="handleWhatsAppAction"
					/>

					<!-- Delete Policy -->
					<MGButton
						text="Delete policy"
						:fullWidth="true"
						:arrow="true"
						@click="showDeleteConfirm = true"
					/>

					<!-- Last updated -->
					<div class="last-updated-row">
						<p v-if="policy.lastUpdatedDateTime" class="last-updated">Last updated: {{ formatDate(policy.lastUpdatedDateTime) }}.</p>
					</div>

				</template>

				<!-- ===== EDIT MODE ===== -->
				<template v-if="editMode">

					<h2 class="section-title">Policy details</h2>
					<InputSections
						v-if="sections.length"
						ref="inputSections"
						:sections="sections"
						:formatter="formatter"
						theme="light"
						:editMode="true"
						:outline="false"
					/>

					<h2 class="section-title">What's insured</h2>
					<p class="section-subtitle">The home, person, pet, or thing this policy is for</p>
					<InputSections
						v-if="policySections.length"
						ref="policySectionsInput"
						:sections="policySections"
						:formatter="formatter"
						theme="light"
						:editMode="true"
						:outline="false"
					/>

					<h2 class="section-title">Cover & limits</h2>
					<p class="section-subtitle">What's covered and where the limits apply</p>
					<InputSections
						v-if="coverSections.length"
						ref="coverSectionsInput"
						:sections="coverSections"
						:formatter="formatter"
						theme="light"
						:editMode="true"
						:outline="false"
					/>

					<!-- Save Button -->
					<div class="anchor-bottom-no-nav">
						<MGButton text="Save changes" :fullWidth="true" @click="saveChanges" />
					</div>

				</template>
			</div>

			<!-- ==================== DOCUMENTS TAB ==================== -->
			<div v-if="activeTab === 'documents'" class="summary-content">

				<!-- Suggested asset documents -->
				<template v-if="isSuggested">
					<div class="linked-documents-header">
						<p class="linked-documents-title">Linked documents</p>
						<p class="linked-documents-subtitle">Documents that were used to create this policy.</p>
					</div>
					<div v-if="suggestedAsset?.relatedDocuments?.length > 0" class="linked-documents-list">
						<div v-for="doc in suggestedAsset.relatedDocuments" :key="doc.id" class="linked-document-item">
							<div class="linked-document-info">
								<p class="linked-document-name">{{ doc.documentSubject || 'Document' }}</p>
								<p class="linked-document-date">{{ doc.createDateTimeString || '' }}</p>
							</div>
							<a :href="'/#/documents/' + doc.id" class="linked-document-view">View</a>
						</div>
					</div>
					<div v-else class="no-documents">
						<p>No documents linked to this policy.</p>
					</div>
					<WhatsAppPromoCard
						ref="whatsAppPromoCardDocs"
						:showImage="true"
						title="Send a document or photo"
						subtitle="We'll add the key details and save the document securely. You can also chat with our WhatsApp assistant."
						:closable="false"
						@action="handleWhatsAppAction"
					/>
				</template>

				<!-- Regular policy documents -->
				<template v-else>
					<div v-if="documents && documents.length > 0" class="documents-list">
						<a v-for="d in documents" :key="d.id" :href="d.url" class="document-item">
							<AccountListItem
								:provider-name="d.name || 'Document'"
								:line1="d.createDateTimeString || ''"
								line2=""
								value=""
								label=""
								:avatar-url="require('@/assets/round1.png')"
							/>
						</a>
					</div>
					<div v-else class="no-documents">
						<p>No documents linked to this policy yet.</p>
						<WhatsAppPromoCard
							ref="whatsAppPromoCardDocs"
							title="Send a document or photo"
							subtitle="We'll add the key details and save the document securely. You can also chat with our WhatsApp assistant."
							:closable="false"
							@action="handleWhatsAppAction"
						/>
					</div>
				</template>
			</div>

		</div>

	<!-- Delete confirmation dialog -->
	<MGDialog v-model="showDeleteConfirm" title="Delete policy" maxWidth="500px" contentHeight="auto" :persistent="false">
		<template #content>
			<p v-if="isSuggested">This will delete any documents you uploaded for this policy. This operation cannot be undone.</p>
			<p v-else>Are you sure you want to delete this policy? This action cannot be undone.</p>
		</template>
		<template #actions>
			<MGButton :fullWidth="true" @click="confirmDelete" :text="deleting ? 'Deleting...' : 'Delete'" :disabled="deleting" />
			<MGButton :stacked="true" :fullWidth="true" @click="showDeleteConfirm = false" :outline="true" text="Cancel" :disabled="deleting" />
		</template>
	</MGDialog>

	<WhatsAppEnableDrawer
		ref="whatsAppEnableDrawer"
		:phoneNumber="customer?.person?.mobilePhone || ''"
		@enable="handleEnableWhatsApp"
		@done="handleWhatsAppEnableDone"
		@closed="handleWhatsAppDrawerClosed"
	/>

</template>

<script>
import Services from '../mixins/Services';
import Helpers from '../mixins/Helpers';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import AccountListItem from './elements/AccountListItem.vue';
import ProgressDial from './elements/ProgressDial.vue';
import InputSections from './elements/InputSections.vue';
import MGButton from './elements/MGButton.vue';
import MGDialog from './elements/MGDialog.vue';
import WhatsAppPromoCard from './elements/WhatsAppPromoCard.vue';
import WhatsAppEnableDrawer from './elements/WhatsAppEnableDrawer.vue';
import WhatsAppMixin from '../mixins/WhatsAppMixin';

export default {
	mixins: [Services, Helpers, WhatsAppMixin],
	components: { CustomerLoadWait, HeaderBlock, AccountListItem, ProgressDial, InputSections, MGButton, MGDialog, WhatsAppPromoCard, WhatsAppEnableDrawer },
	data() {
		return {
			activeTab: 'summary',
			editMode: false,
			showAdditional: false,
			showWhatsappCard: true,
			showDeleteConfirm: false,
			deleting: false,
			sections: [],
			policySections: [],
			coverSections: [],
			suggestedAsset: null
		}
	},
	computed: {
		isBuildingsOrContents() {
			const name = this.policy?.policyType?.name;
			return name === 'Contents' || name === 'Buildings' || name === 'Buildings & Contents';
		},
		insuredAddress() {
			const addr = this.policy?.insuredItems?.[0]?.addressInsured;
			if (!addr) return null;
			return [addr.line1, addr.line2, addr.postCode].filter(Boolean).join(', ');
		},
		hasInsuredDetails() {
			if (!this.policy?.insuredItems?.length) return false;
			const item = this.policy.insuredItems[0];
			const name = this.policy.policyType?.name;
			if (this.isBuildingsOrContents) return !!(item.addressInsured || item.valueInsured);
			if (name === 'Vehicle') return !!(item.vehicleMake || item.vehicleModel || item.vehicleRegistration);
			if (name === 'Pet') return !!(item.name || item.petBreed);
			return false;
		},
		hasCoverDetails() {
			return !!(this.policy?.annualPremium || this.policy?.monthlyPremium || this.policy?.sumAssured || this.policy?.excess || this.policy?.voluntaryExcess);
		},
		hasAdditionalDetails() {
			return !!(this.policy?.soleOrJoint || this.policy?.travelCoverType || this.policy?.travelCoverage || this.policy?.coverType || this.policy?.noClaimsYears);
		},
		headerActionTop() {
			const safeTop = parseInt(this.$cookies.get('st') ?? 0);
			return 16 + safeTop;
		},
		isSuggested() {
			return this.$route.params.id === 'suggested';
		},
		cardLabel() {
			const ui = this.suggestedAsset?.uiElement;
			if (ui) return ui.rightTopValue ? ui.rightTopLabel : '';
			return this.policy?.endDate ? 'Renewal' : '';
		},
		cardValue() {
			const ui = this.suggestedAsset?.uiElement;
			if (ui) return ui.rightTopValue || '';
			return this.policy?.endDate ? this.formatDate(this.policy.endDate) : '';
		},
		cardLabel2() {
			const ui = this.suggestedAsset?.uiElement;
			if (ui) return ui.rightBottomValue ? ui.rightBottomLabel : '';
			return '';
		},
		cardValue2() {
			const ui = this.suggestedAsset?.uiElement;
			if (ui) return ui.rightBottomValue || '';
			return '';
		},
		suggestedFields() {
			if (!this.suggestedAsset) return [];
			const a = this.suggestedAsset;
			const fields = [];
			if (a.provider) fields.push({ label: 'Provider', value: a.provider });
			if (a.assetType) fields.push({ label: 'Type', value: a.assetType.replace(/([A-Z])/g, ' $1').trim() });
			if (a.accountNumber) fields.push({ label: 'Reference number', value: a.accountNumber });
			if (a.toDate) fields.push({ label: 'Renewal', value: this.formatDate(a.toDate) });
			if (a.balanceOrCost) fields.push({ label: 'Premium', value: this.formatter.format(parseFloat(a.balanceOrCost.replace(/[^0-9.-]/g, ''))) });
			if (a.registrationNumber) fields.push({ label: 'Vehicle registration', value: a.registrationNumber });
			if (a.address) fields.push({ label: 'Address', value: a.address });
			if (a.rate) fields.push({ label: 'Rate', value: a.rate });
			if (a.fromDate) fields.push({ label: 'Start date', value: this.formatDate(a.fromDate) });
			if (a.toDate) fields.push({ label: 'End date', value: this.formatDate(a.toDate) });
			if (a.termExpiry) fields.push({ label: 'Term expiry', value: this.formatDate(a.termExpiry) });
			return fields;
		}
	},
	methods: {
		properCase(str) {
			if (!str) return str;
			return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
		},
		formatDate(dateString) {
			if (!dateString) return '';
			let date;
			// Handle DD/MM/YYYY format
			const parts = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
			if (parts) {
				date = new Date(parts[3], parts[2] - 1, parts[1]);
			} else {
				date = new Date(dateString);
			}
			if (isNaN(date.getTime())) return dateString;
			return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
		},
		handleHeaderDelete(done) {
			this._deletePolicy(this.$route.params.id, () => {
				this._getInsuranceInfo(null, true);
				if (done) done();
				this.$router.push('/money');
			});
		},
		loadEditSections() {
			this._getAiopSection('InsuranceAdditionalSection.json', () => {
				console.log('Additional section loaded', this.sectionJson);
				this.sections = this.sectionJson;

				// Load provider dropdown items
				this._getInsuranceProviders((res) => {
					res = res.filter(item => item.buildings === true || item.contents === true || item.gadget === true
							|| item.car === true || item.motorbike === true || item.pet === true
							|| item.life === true || item.other === true)
							.filter((item, index, self) =>
							index === self.findIndex((t) => t.name === item.name)
						);
					var providerField = this._findFieldById('provider');
					if (providerField) {
						providerField.items = res.map(provider => ({
							name: provider.name.replace(/\w\S*/g, (txt) => {
								return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
							}),
							logoUri: provider.logoUri
						}));
					}
				});

				this._getAiopSection('InsuranceAdditionalAbout.json', () => {
					const aboutSections = this.sectionJson;

					this._getAiopSection('InsuranceAdditionalCover.json', () => {
						const coverSectionsJson = this.sectionJson;
						const policyType = this.policy.policyType?.name;

						this.policySections = this._filterSectionsByRule(aboutSections, 'policyType', policyType);
						this.coverSections = this._filterSectionsByRule(coverSectionsJson, 'policyType', policyType);

						this.populateFields(this.sections);
						this.populateFields(this.policySections);
						this.populateFields(this.coverSections);

						this.editMode = true;
					});
				});
			});
		},
		populateFields(sections) {
			if (!this.policy) return;

			const providerField = this._findFieldByIdInSections(sections, 'provider');
			if (providerField) providerField.value = this.policy.provider?.name || '';

			const renewalDateField = this._findFieldByIdInSections(sections, 'renewalDate');
			if (renewalDateField) renewalDateField.value = this._dateToString(this.policy.endDate);

			const startDateField = this._findFieldByIdInSections(sections, 'startDate');
			if (startDateField) startDateField.value = this._dateToString(this.policy.startDate);

			const payFrequencyField = this._findFieldByIdInSections(sections, 'payFrequency');
			if (payFrequencyField) {
				payFrequencyField.value = this.policy.payFrequency || (this.policy.annualPremium ? 'Annually' : this.policy.monthlyPremium ? 'Monthly' : null);
			}

			const policyNumberField = this._findFieldByIdInSections(sections, 'policyNumber');
			if (policyNumberField) policyNumberField.value = this.policy.policyNumber || null;

			const premiumField = this._findFieldByIdInSections(sections, 'premium');
			if (premiumField) premiumField.value = this.policy.annualPremium || this.policy.monthlyPremium || null;

			const excessField = this._findFieldByIdInSections(sections, 'excess');
			if (excessField) excessField.value = this.policy.excess || null;

			const voluntaryExcessField = this._findFieldByIdInSections(sections, 'voluntaryExcess');
			if (voluntaryExcessField) voluntaryExcessField.value = this.policy.voluntaryExcess || null;

			const sumInsuredField = this._findFieldByIdInSections(sections, 'sumInsured');
			if (sumInsuredField) sumInsuredField.value = this.policy.sumAssured || null;

			const addressField = this._findFieldByIdInSections(sections, 'address');
			if (addressField && this.policy.insuredItems?.[0]?.addressInsured) {
				addressField.value = this.policy.insuredItems[0].addressInsured;
			}
		},
		findFieldValueInAllSections(fieldId) {
			const allSections = [...this.sections, ...this.policySections, ...this.coverSections];
			return this._findFieldValueByIdInSections(allSections, fieldId);
		},
		saveChanges() {
			const payFrequency = this.findFieldValueInAllSections('payFrequency');
			const policyNumber = this.findFieldValueInAllSections('policyNumber');
			const premium = this.findFieldValueInAllSections('premium');
			const excess = this.findFieldValueInAllSections('excess');
			const voluntaryExcess = this.findFieldValueInAllSections('voluntaryExcess');
			const totalExcess = (parseFloat(excess) || 0) + (parseFloat(voluntaryExcess) || 0);
			const sumInsured = this.findFieldValueInAllSections('sumInsured');

			const addressObject = this.findFieldValueInAllSections('address');
			let insuredItems = this.policy.insuredItems;
			if (addressObject && insuredItems && insuredItems.length > 0) {
				insuredItems[0].addressInsured = {
					line1: addressObject?.line1 || null,
					line2: addressObject?.line2 || null,
					line3: addressObject?.line3 || null,
					town: addressObject?.town || null,
					postCode: addressObject?.postCode || null,
				};
			}

			const renewalDate = this.findFieldValueInAllSections('renewalDate');
			const startDate = this.findFieldValueInAllSections('startDate');

			const updatedPolicy = {
				id: this.$route.params.id,
				policyType: this.policy.policyType,
				provider: this.policy.provider,
				insuredItems: insuredItems,
				startDate: startDate ? this._stringToDate(startDate) : this.policy.startDate,
				endDate: renewalDate ? this._stringToDate(renewalDate) : this.policy.endDate,
				payFrequency: payFrequency || null,
				policyNumber: policyNumber || null,
				annualPremium: payFrequency === 'Annually' ? premium : null,
				monthlyPremium: payFrequency === 'Monthly' ? premium : null,
				excess: totalExcess || this.policy.excess,
				voluntaryExcess: voluntaryExcess || this.policy.voluntaryExcess,
				noClaimsYears: this.policy.noClaimsYears,
				soleOrJoint: this.policy.soleOrJoint,
				sumAssured: sumInsured || this.policy.sumAssured,
				coverType: this.policy.coverType,
				travelCoverType: this.policy.travelCoverType,
				travelCoverage: this.policy.travelCoverage
			};

			this._updatePolicy(updatedPolicy, () => {
				this._getPolicy(this.$route.params.id, null, true);
				this._getInsuranceInfo(null, true);
				this.editMode = false;
			});
		},
		confirmDelete() {
			this.deleting = true;
			if (this.isSuggested && this.suggestedAsset?.relatedDocuments?.length) {
				let remaining = this.suggestedAsset.relatedDocuments.length;
				this.suggestedAsset.relatedDocuments.forEach(doc => {
					this._deleteDocument(doc.id, () => {
						remaining--;
						if (remaining === 0) {
							this._clearDocuments();
							this.$router.push('/money');
						}
					});
				});
			} else {
				this._deletePolicy(this.$route.params.id, () => {
					this._getInsuranceInfo(/*onSuccess*/ null, /*refreshCache*/ true);
					this.$router.push('/money');
				});
			}
		},
		onLoad: async function() {
			this._getCustomer();
			if (this.isSuggested) {
				const assetId = this.$route.query.assetId;
				if (assetId) {
					this._getSuggestedAssets((result) => {
						const asset = result.find(a => a.id === assetId);
						if (asset) {
							this.suggestedAsset = asset;
							const a = this.suggestedAsset;
							// Build a mock policy object so the page renders
							this.policy = {
								provider: { name: a.provider || 'Provider' },
								policyType: { name: a.assetType ? a.assetType.replace(/([A-Z])/g, ' $1').trim() : 'Insurance' },
								policyNumber: a.accountNumber || '',
								endDate: a.toDate || null,
								startDate: a.fromDate || null
							};
						} else {
							this.$router.push('/money');
						}
					});
				} else {
					this.$router.push('/money');
				}
			} else {
				this._getPolicy(this.$route.params.id, () => {
					this._getDocumentsByHubId(this.policy.id);
				});
			}
		}
	},
	watch: {
		activeTab(val) {
			const hash = window.location.hash.split('?')[0];
			const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
			params.set('tab', val);
			const newUrl = window.location.pathname + window.location.search + hash + '?' + params.toString();
			window.history.replaceState(history.state, '', newUrl);
		}
	},
	mounted: function () {
		const tab = this.$route.query.tab;
		if (tab === 'summary' || tab === 'documents') {
			this.activeTab = tab;
		}
		this.onLoad();
	}
}
</script>

<style scoped>

.policy-page {
	padding-bottom: 100px;
}

/* Policy card */
.policy-card-container {
	margin-left: -30px;
	margin-right: -30px;
	padding-bottom: 20px;
}

.policy-card-container :deep(.arrow-icon) {
	display: none;
}

/* Summary content area */
.summary-content {
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 24px 0;
}

/* Section groups */
.section-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.section-title {
	font-size: 15px;
	font-weight: bold;
	margin: 0;
	color: var(--text-color);
}

.section-subtitle {
	font-size: 13px;
	color: var(--text-color);
	margin: 0;
}

/* Field cards - each field is its own card */
.fields-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.field-card {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 4px;
	padding: 16px;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 8px;
}

.field-label {
	font-size: 13px;
	color: var(--text-color);
}

.field-value {
	font-size: 13px;
	color: var(--text-color);
	text-align: right;
}

/* Additional details section */
.additional-section {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 16px 0;
	border-top: 1px solid rgba(65, 54, 85, 0.1);
}

.additional-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

/* Last updated row */
.last-updated-row {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
}

.last-updated {
	font-size: 11px;
	color: var(--text-color);
	text-align: center;
	margin: 0;
}

/* Documents tab */
.documents-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
}

.documents-list a {
	text-decoration: none;
	color: inherit;
}

.no-documents {
	text-align: center;
	width: 100%;
}

.no-documents p {
	font-size: 14px;
	color: var(--text-color);
	margin-bottom: 24px;
}

/* Header action button */
.header-action-btn {
	position: fixed;
	right: 20px;
	z-index: 101;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: white;
	border-radius: 50%;
}

/* Tabs */
.tabs {
	display: flex;
	gap: 8px;
	padding: 0 0 10px;
	margin-left: -10px;
	margin-right: -10px;
}

.tab {
	flex: 1;
	text-align: center;
	padding: 12px 24px 14px;
	font-size: 13px;
	color: var(--text-color);
	background-color: white;
	border-radius: 6px;
}

.tab.active {
	background: var(--background-color-alt2);
	color: white;
}

/* Suggested asset banner */
.suggested-banner-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.suggested-banner {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	border-radius: 8px;
	background: linear-gradient(to right, #E1C6C8, #FCE5D4);
}

.suggested-banner-icon {
	width: 16px;
	height: 16px;
}

.suggested-banner-text {
	font-size: 13px;
	font-weight: bold;
	color: var(--text-color);
}

.suggested-banner-description {
	font-size: 13px;
	color: var(--text-color);
	margin: 0;
	line-height: 16px;
}

.suggested-link {
	font-weight: bold;
	color: var(--text-color);
}

/* Suggested asset field cards (label above value, like DocumentPage) */
.suggested-field-card {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 16px;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 8px;
}

.suggested-field-label {
	font-size: 13px;
	line-height: 16px;
	color: var(--text-color);
}

.suggested-field-value {
	font-size: 13px;
	line-height: 16px;
	font-weight: bold;
	color: var(--text-color);
}

/* Linked documents (suggested asset documents tab) */
.linked-documents-header {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.linked-documents-title {
	font-size: 13px;
	font-weight: bold;
	color: var(--text-color);
	margin: 0;
}

.linked-documents-subtitle {
	font-size: 13px;
	color: var(--text-color);
	margin: 0;
}

.linked-documents-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.linked-document-item {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 12px;
	background: white;
	border-radius: 8px;
}

.linked-document-info {
	flex: 1;
}

.linked-document-name {
	font-size: 12px;
	color: var(--text-color);
	margin: 0;
	word-break: break-all;
}

.linked-document-date {
	font-size: 11px;
	color: var(--text-color);
	margin: 4px 0 0;
	opacity: 0.6;
}

.linked-document-view {
	font-size: 13px;
	font-weight: bold;
	color: var(--text-color);
	text-decoration: underline;
	flex-shrink: 0;
}

</style>
